import { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Stack,
  Grid,
  Chip,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  LinearProgress,
  useMediaQuery,
  useTheme,
  Drawer,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SortIcon from "@mui/icons-material/Sort";
import CreditoClienteDetalle from "./CreditoClienteDetalle";
import KpiCard from "./ui/KpiCard";
import SectionHeader from "./ui/SectionHeader";
import { SEMANTIC } from "./theme/brand";

const ESTADO_CONFIG = {
  vencido: { color: SEMANTIC.danger, bg: SEMANTIC.dangerTint, icon: <ErrorIcon sx={{ fontSize: 16, color: SEMANTIC.danger }} />, label: "Vencido" },
  proximo: { color: SEMANTIC.warning, bg: SEMANTIC.warningTint, icon: <WarningAmberIcon sx={{ fontSize: 16, color: SEMANTIC.warning }} />, label: "Por vencer" },
  ok: { color: SEMANTIC.revenue, bg: SEMANTIC.revenueTint, icon: <CheckCircleIcon sx={{ fontSize: 16, color: SEMANTIC.revenue }} />, label: "Al día" },
};

function getEstado(cliente) {
  if (!cliente.fechaLimite) return "ok";
  const hoy = new Date().toISOString().slice(0, 10);
  const en3 = new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10);
  if (cliente.fechaLimite < hoy) return "vencido";
  if (cliente.fechaLimite <= en3) return "proximo";
  return "ok";
}

function ClienteCard({ cliente, moneda, selected, onClick }) {
  const estado = getEstado(cliente);
  const cfg = ESTADO_CONFIG[estado];
  const pctPagado = cliente.totalDeuda > 0 ? (cliente.totalPagado / cliente.totalDeuda) * 100 : 0;

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: selected ? "primary.main" : cfg.color + "40",
        borderWidth: selected ? 2 : 1,
        borderRadius: 2.5,
        transition: "all 0.15s",
        "&:hover": { borderColor: "primary.main", boxShadow: 1 },
      }}
    >
      <CardActionArea onClick={onClick} sx={{ p: 0 }}>
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          {/* Header: name + status chip */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.75}>
            <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ flex: 1, mr: 1 }}>
              {cliente.nombre}
            </Typography>
            <Chip
              icon={cfg.icon}
              label={cfg.label}
              size="small"
              sx={{
                height: 22,
                fontSize: 10,
                fontWeight: 700,
                bgcolor: cfg.bg,
                color: cfg.color,
                "& .MuiChip-icon": { ml: 0.5 },
              }}
            />
          </Stack>

          {/* Money row */}
          <Stack direction="row" justifyContent="space-between" alignItems="baseline" mb={0.5}>
            <Typography variant="h6" component="p" fontWeight={800} color={cfg.color}>
              {moneda} {cliente.saldoPendiente.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              de {moneda} {cliente.totalDeuda.toLocaleString()}
            </Typography>
          </Stack>

          {/* Progress bar */}
          <LinearProgress
            variant="determinate"
            value={Math.min(100, pctPagado)}
            color={estado === "vencido" ? "error" : estado === "proximo" ? "warning" : "success"}
            sx={{ height: 5, borderRadius: 3, mb: 0.5 }}
          />

          {/* Footer: date + payments info */}
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              Pagado: {Math.round(pctPagado)}%
            </Typography>
            {cliente.fechaLimite && (
              <Typography variant="caption" color={cfg.color} fontWeight={600}>
                Vence: {cliente.fechaLimite}
              </Typography>
            )}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

export default function CreditosPanel({ store }) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [selectedKey, setSelectedKey] = useState(null);
  const [filtro, setFiltro] = useState("todos");
  const [busqueda, setBusqueda] = useState("");
  const [sortBy, setSortBy] = useState("fecha"); // fecha | monto | nombre

  const clientes = store.clientesConDeuda;

  const clientesFiltrados = useMemo(() => {
    let list = [...clientes];

    // Filter by status
    if (filtro === "vencido") list = list.filter(c => getEstado(c) === "vencido");
    else if (filtro === "proximo") list = list.filter(c => getEstado(c) === "proximo");
    else if (filtro === "ok") list = list.filter(c => getEstado(c) === "ok");

    // Search
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      list = list.filter(c => c.nombre.toLowerCase().includes(q));
    }

    // Sort
    if (sortBy === "monto") list.sort((a, b) => b.saldoPendiente - a.saldoPendiente);
    else if (sortBy === "nombre") list.sort((a, b) => a.nombre.localeCompare(b.nombre));
    // default: fecha (already sorted by fechaLimite from store)

    return list;
  }, [clientes, filtro, busqueda, sortBy]);

  const selectedCliente = clientes.find(c => c.key === selectedKey) || null;

  // Stats
  const totalDeuda = store.deudaTotal;
  const vencidos = clientes.filter(c => getEstado(c) === "vencido");
  const proximos = clientes.filter(c => getEstado(c) === "proximo");
  const deudaVencida = vencidos.reduce((s, c) => s + c.saldoPendiente, 0);

  const handleSelect = (key) => {
    setSelectedKey(key === selectedKey ? null : key);
  };

  const detalleContent = selectedCliente ? (
    <CreditoClienteDetalle
      cliente={selectedCliente}
      store={store}
      onClose={() => setSelectedKey(null)}
    />
  ) : (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", p: 4 }}>
      <Typography color="text.secondary" textAlign="center">
        Selecciona un cliente para ver detalle
      </Typography>
    </Box>
  );

  return (
    <Box>
      {/* Header */}
      <SectionHeader
        title="Créditos y cobranzas"
        description="Saldo pendiente de ventas al crédito. Prioriza los vencidos."
        sx={{ mb: 2 }}
      />

      <Grid container spacing={1.5} mb={2.5}>
        <Grid item xs={6} md={3}>
          <KpiCard tone="potential" label="Total por cobrar" value={`${store.config.moneda} ${totalDeuda.toLocaleString()}`} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard tone="danger" label="Vencido" value={`${store.config.moneda} ${deudaVencida.toLocaleString()}`} helper={`${vencidos.length} ${vencidos.length === 1 ? "cliente" : "clientes"}`} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard tone="warning" label="Vence en 3 días" value={proximos.length} helper={proximos.length === 1 ? "cliente" : "clientes"} />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiCard tone="neutral" label="Clientes con saldo" value={clientes.length} />
        </Grid>
      </Grid>

      {clientes.length === 0 ? (
        <Box textAlign="center" py={6}>
          <CheckCircleIcon sx={{ fontSize: 40, color: "success.main" }} />
          <Typography variant="h6" component="p" color="text.primary" mt={1}>Sin deudas pendientes</Typography>
          <Typography variant="body2" color="text.secondary" mt={1}>
            Las ventas a crédito aparecerán aquí
          </Typography>
        </Box>
      ) : (
        <>
          {/* Filters bar */}
          <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap>
            <TextField
              placeholder="Buscar cliente..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              size="small"
              sx={{ flex: 1, minWidth: 140 }}
            />
            <TextField
              value={filtro}
              onChange={(e) => setFiltro(e.target.value)}
              select
              size="small"
              label="Estado"
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="todos">Todos ({clientes.length})</MenuItem>
              <MenuItem value="vencido">Vencidos ({vencidos.length})</MenuItem>
              <MenuItem value="proximo">Por vencer ({proximos.length})</MenuItem>
              <MenuItem value="ok">Al día ({clientes.length - vencidos.length - proximos.length})</MenuItem>
            </TextField>
            <Tooltip title="Ordenar">
              <TextField
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                select
                size="small"
                label="Ordenar por"
                sx={{ minWidth: 110 }}
                InputProps={{ startAdornment: <SortIcon sx={{ fontSize: 16, mr: 0.5, opacity: 0.5 }} /> }}
              >
                <MenuItem value="fecha">Fecha</MenuItem>
                <MenuItem value="monto">Monto</MenuItem>
                <MenuItem value="nombre">Nombre</MenuItem>
              </TextField>
            </Tooltip>
          </Stack>

          {/* Desktop: split view. Mobile: cards + bottom drawer */}
          {isDesktop ? (
            <Grid container spacing={2} sx={{ minHeight: 400 }}>
              <Grid item xs={12} md={5}>
                <Stack spacing={1} sx={{ maxHeight: 600, overflowY: "auto", pr: 0.5 }}>
                  {clientesFiltrados.map(c => (
                    <ClienteCard
                      key={c.key}
                      cliente={c}
                      moneda={store.config.moneda}
                      selected={selectedKey === c.key}
                      onClick={() => handleSelect(c.key)}
                    />
                  ))}
                  {clientesFiltrados.length === 0 && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                      Sin resultados
                    </Typography>
                  )}
                </Stack>
              </Grid>
              <Grid item xs={12} md={7}>
                <Card variant="outlined" sx={{ height: "100%", minHeight: 400, borderRadius: 2.5 }}>
                  {detalleContent}
                </Card>
              </Grid>
            </Grid>
          ) : (
            <>
              {/* Mobile: cards list */}
              <Stack spacing={1.5}>
                {clientesFiltrados.map(c => (
                  <ClienteCard
                    key={c.key}
                    cliente={c}
                    moneda={store.config.moneda}
                    selected={selectedKey === c.key}
                    onClick={() => handleSelect(c.key)}
                  />
                ))}
                {clientesFiltrados.length === 0 && (
                  <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                    Sin resultados
                  </Typography>
                )}
              </Stack>

              {/* Mobile drawer */}
              <Drawer
                anchor="bottom"
                open={!!selectedCliente}
                onClose={() => setSelectedKey(null)}
                PaperProps={{ sx: { borderRadius: "16px 16px 0 0", maxHeight: "85vh" } }}
              >
                {selectedCliente && (
                  <CreditoClienteDetalle
                    cliente={selectedCliente}
                    store={store}
                    onClose={() => setSelectedKey(null)}
                  />
                )}
              </Drawer>
            </>
          )}
        </>
      )}
    </Box>
  );
}
